import { PiletApi } from '@sharefiledev/sharefile-appshell';
import {
	AiAgentConfigAudience,
	AiAgentConfigValue,
	DataPageResult,
} from '../models/models';
import {
	initializeClient,
	SafRegistryClient,
	SafRegistryClientConfiguration,
} from './client';
import { AiAgentConfigValueNotFoundError } from './configValueNotFoundError';
import { SafRegistryError } from './errors';
import { SafNetworkError } from './networkError';
import { SafRequestAbortedError } from './requestAbortedError';

/**
 * SafRegistry SDK for managing AI agent configuration values
 */
export class SafRegistrySdk {
	private client: SafRegistryClient | undefined;

	public constructor() {}

	public initialize(config: SafRegistryClientConfiguration) {
		this.client = initializeClient(config);
	}

	/**
	 * List AI agent config values by agent namespace and name
	 * @param audience The audience for which the config is intended
	 * @param namespace The agent namespace
	 * @param name The agent name
	 * @param ac AbortController instance to allow cancelling the request (optional, defaults to 30s timeout)
	 * @returns Promise resolving to a paged list of config values
	 * @throws {InvalidRequestError} When required parameters are missing or invalid
	 * @throws {UnauthorizedError} When user lacks permission to read config values
	 * @throws {ServerError} When server encounters an error
	 * @throws {SafRequestAbortedError} When request is aborted
	 * @throws {NetworkError} When network error occurs
	 */
	public async listConfigValuesByAgent(
		audience: AiAgentConfigAudience,
		namespace: string,
		name: string,
		ac?: AbortController
	): Promise<DataPageResult<AiAgentConfigValue>> {
		this.validateRequiredParams({ audience, namespace, name });

		// Create default AbortController with 30 second timeout if not provided
		const abortController = ac || this.createDefaultAbortController();

		try {
			return await this.client!.listConfigValuesByAgent(
				audience,
				namespace,
				name,
				abortController
			);
		} catch (error) {
			throw this.handleError(error, 'list config values');
		}
	}

	/**
	 * Get a specific AI agent config value by key
	 * @param key The config key
	 * @param namespace The agent namespace
	 * @param name The agent name
	 * @param ac AbortController instance to allow cancelling the request (optional, defaults to 30s timeout)
	 * @returns Promise resolving to the config value
	 * @throws {ConfigValueNotFoundError} When config value is not found
	 * @throws {InvalidRequestError} When required parameters are missing or invalid
	 * @throws {UnauthorizedError} When user lacks permission to read config values
	 * @throws {ServerError} When server encounters an error
	 * @throws {SafRequestAbortedError} When request is aborted
	 * @throws {NetworkError} When network error occurs
	 */
	public async getConfigValueByKey(
		key: string,
		namespace: string,
		name: string,
		ac?: AbortController
	): Promise<AiAgentConfigValue> {
		this.validateRequiredParams({ key, namespace, name });

		// Create default AbortController with 30 second timeout if not provided
		const abortController = ac || this.createDefaultAbortController();

		try {
			const result = await this.client!.getConfigValueByKey(
				key,
				namespace,
				name,
				abortController
			);
			if (result === null) {
				throw new AiAgentConfigValueNotFoundError(key, namespace, name);
			}
			return result;
		} catch (error) {
			if (error instanceof SafRegistryError) {
				throw error;
			}
			throw this.handleError(error, 'get config value');
		}
	}

	/**
	 * Set or update an AI agent config value
	 * @param configValue The config value to set
	 * @param ac AbortController instance to allow cancelling the request (optional, defaults to 30s timeout)
	 * @returns Promise resolving to the updated config value
	 * @throws {InvalidRequestError} When config value is invalid
	 * @throws {UnauthorizedError} When user lacks permission to write config values
	 * @throws {ServerError} When server encounters an error
	 * @throws {SafRequestAbortedError} When request is aborted
	 * @throws {NetworkError} When network error occurs
	 */
	public async setConfigValue(
		configValue: AiAgentConfigValue,
		ac?: AbortController
	): Promise<AiAgentConfigValue> {
		this.validateConfigValue(configValue);

		// Create default AbortController with 30 second timeout if not provided
		const abortController = ac || this.createDefaultAbortController();

		try {
			return await this.client!.setConfigValue(configValue, abortController);
		} catch (error) {
			throw this.handleError(error, 'set config value');
		}
	}

	/**
	 * Create a default AbortController with a 30 second timeout
	 */
	private createDefaultAbortController(): AbortController {
		const controller = new AbortController();
		setTimeout(() => {
			controller.abort();
		}, 30000); // 30 seconds
		return controller;
	}

	/**
	 * Validate required parameters
	 */
	private validateRequiredParams(params: Record<string, unknown>): void {
		for (const [key, value] of Object.entries(params)) {
			if (!value || (typeof value === 'string' && value.trim() === '')) {
				throw new SafRegistryError(`${key} is required and cannot be empty`);
			}
		}
	}

	/**
	 * Validate config value object
	 */
	private validateConfigValue(configValue: AiAgentConfigValue): void {
		if (!configValue) {
			throw new SafRegistryError('Config value is required');
		}
		if (!configValue.key || configValue.key.trim() === '') {
			throw new SafRegistryError('Config value key is required and cannot be empty');
		}
		if (!configValue.agentRID || configValue.agentRID.trim() === '') {
			throw new SafRegistryError('Config value agentRID is required and cannot be empty');
		}
	}

	/**
	 * Convert HTTP errors to custom SafRegistry errors
	 */
	private handleError(error: unknown, operation: string): SafRegistryError {
		// Handle abort errors
		if (error instanceof Error && error.name === 'AbortError') {
			return new SafRequestAbortedError(error);
		}

		// Handle network errors
		if (error instanceof Error) {
			if (error.message.includes('network') || error.message.includes('fetch')) {
				return new SafNetworkError(error.message, error);
			}
		}

		return new SafRegistryError(operation + ' failed', error);
	}
}

export const safRegistrySdk = new SafRegistrySdk();

export function configureSafRegistrySdk(app: PiletApi) {
	const config: SafRegistryClientConfiguration = {
		baseUrl: getApiBaseUrl(app),
	};
	safRegistrySdk.initialize(config);
}

function getApiBaseUrl(app: PiletApi) {
	let apiOrigin = window.origin;

	try {
		if (typeof app.meta.config.apiOrigin === 'string') {
			apiOrigin = app.meta.config.apiOrigin;
		}
	} catch (_error) {
		// Do nothing (condition under try block is only meaningful for test/dev environment)
	}

	return `${apiOrigin}/io/safregistry`;
}
